import React, { useEffect, useState } from "react"
import { nextRound } from "./ballparkRoom"
import { TOO_HIGH } from "../../../utils/ballparkLogic"
import { Screen, BpButton, PlayerDot, C, F } from "./BallparkUI"
import { playSound } from "../../../utils/ballparkSounds"

const RevealScreen = ({ room, code, isHost, setShowScores }) => {
  const r = room.lastResult
  const [showHistory, setShowHistory] = useState(false)

  useEffect(() => {
    if (r) {
      if (r.winning === TOO_HIGH) {
        playSound("lose")
      } else {
        playSound("win")
      }
    }
  }, [r])

  if (!r) return null
  const players = Object.entries(room.players)
  const lastRound = room.round >= room.totalRounds

  const betSummary = (pid) => {
    const placed = room.bets?.[pid] || []
    if (!placed.length) return "no bets"
    return placed
      .map((b) => {
        const label = b.slot === TOO_HIGH ? "too high" : r.slots[b.slot]?.value
        const primary = b.primary !== undefined ? b.primary : (b.chips || 0)
        const stacked = b.stacked || 0
        if (stacked > 0) {
          return `${primary} token${primary > 1 ? "s" : ""} + ${stacked} stacked on ${label}`
        }
        return `${primary} token${primary > 1 ? "s" : ""} on ${label}`
      })
      .join(" · ")
  }

  return (
    <Screen dark style={{ gap: 18 }}>
      {/* Top Header Row with Round and Standings Button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: C.tan }}>
            Round {room.round} of {room.totalRounds}
          </span>
        </div>
        <button
          onClick={() => {
            playSound("click")
            setShowScores(true)
          }}
          style={{
            background: C.darkCard,
            border: `1.5px solid ${C.muted}`,
            borderRadius: 12,
            padding: "4px 10px",
            fontFamily: F.display,
            fontWeight: 800,
            fontSize: 12,
            color: C.bg,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🏆 Scores
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 2, color: C.gold }}>
          THE ANSWER IS
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 80, lineHeight: 1, marginTop: 8 }}>
          {r.answer}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.tan, marginTop: 10 }}>
          {r.note}
        </div>
      </div>

      <div
        style={{
          background: C.highlight,
          color: C.ink,
          borderRadius: 18,
          padding: "16px 18px",
          boxShadow: `0 0 0 3px ${C.gold}`,
        }}
      >
        {r.winning === TOO_HIGH ? (
          <div style={{ fontWeight: 800, fontSize: 16 }}>
            Everyone guessed too high!
            <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
              Bets on "too high" pay {r.winOdds}×
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {r.winnerIds.map((pid, idx) => (
                <span key={pid} style={{ marginLeft: idx ? -8 : 0, display: "inline-flex" }}>
                  <PlayerDot color={room.players[pid]?.color} size={28} />
                </span>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                {r.winnerIds.map((pid) => room.players[pid]?.name).join(" & ")} win
                {r.winnerIds.length > 1 ? "" : "s"} the round
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
                Guessed {r.slots[r.winning].value} — closest without going over
              </div>
            </div>
            <div style={{ marginLeft: "auto", fontFamily: F.display, fontWeight: 800, fontSize: 22, color: C.rust }}>
              +3
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.tan }}>
          PAYOUTS
        </div>
        {players.map(([pid, p]) => {
          const delta = r.deltas[pid] || 0
          return (
            <div
              key={pid}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: C.darkCard,
                borderRadius: 14,
                padding: "12px 14px",
                opacity: delta > 0 ? 1 : 0.6,
              }}
            >
              <PlayerDot color={p.color} />
              <span style={{ fontWeight: 800, fontSize: 15 }}>{p.name}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.tan }}>
                {betSummary(pid)}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: F.display,
                  fontWeight: 800,
                  fontSize: 20,
                  color: delta > 0 ? C.gold : C.tan,
                }}
              >
                +{delta}
              </span>
            </div>
          )
        })}
      </div>

      {room.history && room.history.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            onClick={() => {
              playSound("click")
              setShowHistory(!showHistory)
            }}
            style={{
              background: "transparent",
              border: "none",
              color: C.tan,
              fontFamily: F.display,
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              margin: "0 auto",
              outline: "none",
            }}
          >
            {showHistory ? "▼ Hide Game History" : "▶ View Game History"}
          </button>
          {showHistory && (
            <div
              style={{
                marginTop: 4,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                background: C.darkCard,
                borderRadius: 16,
                padding: 14,
                maxHeight: 180,
                overflowY: "auto",
                border: `1.5px solid ${C.muted}`,
              }}
            >
              {room.history.map((h, idx) => (
                <div
                  key={idx}
                  style={{
                    borderBottom: idx < room.history.length - 1 ? `1px dashed ${C.muted}` : "none",
                    paddingBottom: idx < room.history.length - 1 ? 10 : 0,
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 800, color: C.gold }}>
                    Round {h.round}: {h.question}
                  </div>
                  <div style={{ color: C.bg, marginTop: 4, fontWeight: 700 }}>
                    Correct Answer: <span style={{ color: C.gold, fontSize: 15 }}>{h.answer}</span>
                  </div>
                  <div style={{ color: C.tan, fontSize: 12, marginTop: 2, lineHeight: 1.3 }}>
                    {h.note}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: "auto" }}>
        {isHost ? (
          <BpButton
            variant="gold"
            onClick={() => {
              playSound("click")
              nextRound(code)
            }}
          >
            {lastRound ? "Final scores →" : "Next round →"}
          </BpButton>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.tan }}>
            Waiting for the host…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default RevealScreen
